import React from 'react';
import { Film, MessageCircle, Instagram, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui/card';

const LinksBio = () => {
  const profileImage = 'https://customer-assets.emergentagent.com/job_filmmaker-hub-11/artifacts/g85zg5kd_Captura%20de%20tela%202025-12-01%20163641.png';
  
  const links = [
    {
      id: 1,
      title: 'Pack Cinematográfico Premium',
      subtitle: 'B-rolls de cenas de filmes para elevar seus edits ao nível profissional',
      url: 'https://packcinematografico.com/',
      icon: Film,
      featured: true,
    },
    {
      id: 2,
      title: 'Orçamento aqui',
      subtitle: 'Solicite um orçamento personalizado para seus projetos.',
      url: 'https://wa.me/message/KU3HPTWQRTJ7K1',
      icon: MessageCircle,
      featured: false,
    },
    {
      id: 3,
      title: 'Portfólio',
      subtitle: 'Veja meus melhores trabalhos e cases.',
      url: 'https://www.instagram.com/stories/highlights/18000524270833636/',
      icon: Instagram,
      featured: false,
    },
  ];

  const handleCardClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-[#0a0a0a]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-600/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Header Section */}
          <div className="text-center mb-10 animate-fade-in-up">
            {/* Avatar with enhanced styling */}
            <div className="mb-6 flex justify-center">
              <div className="relative group">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 rounded-full blur-md opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Avatar container */}
                <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-orange-500 to-red-600 p-1">
                  <div className="w-full h-full rounded-full overflow-hidden bg-zinc-900 border-2 border-zinc-800">
                    <img 
                      src={profileImage}
                      alt="Diorgy Santos"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"></path></svg></div>';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Name with gradient */}
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-100 to-zinc-300 mb-3 tracking-tight">
              Diorgy Santos
            </h1>
            
            {/* Subtitle with line decoration */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-orange-500" />
              <p className="text-zinc-400 text-sm font-semibold tracking-widest uppercase">
                Editor de vídeo & Creator
              </p>
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-orange-500" />
            </div>
            
            {/* Additional tagline */}
            <p className="text-zinc-600 text-xs mt-2">
              Transformando ideias em experiências visuais
            </p>
          </div>

          {/* Cards Section with stagger animation */}
          <div className="space-y-4 mb-8">
            {links.map((link, index) => {
              const Icon = link.icon;
              return (
                <div
                  key={link.id}
                  className="animate-fade-in-up"
                  style={{
                    animationDelay: `${(index + 1) * 150}ms`,
                  }}
                >
                  <Card
                    onClick={() => handleCardClick(link.url)}
                    className={link.featured ? 'link-card-featured-v2' : 'link-card-secondary-v2'}
                  >
                    <div className="relative">
                      {/* Content wrapper */}
                      <div className="flex items-center gap-4 p-6">
                        {/* Icon with enhanced styling */}
                        <div className={link.featured ? 'icon-featured' : 'icon-secondary'}>
                          <Icon className="w-6 h-6" strokeWidth={2.5} />
                        </div>

                        {/* Text content */}
                        <div className="flex-1 min-w-0">
                          <h3 className={link.featured ? 'title-featured' : 'title-secondary'}>
                            {link.title}
                          </h3>
                          <p className={link.featured ? 'subtitle-featured' : 'subtitle-secondary'}>
                            {link.subtitle}
                          </p>
                        </div>

                        {/* Arrow icon */}
                        <ExternalLink 
                          className={link.featured ? 'w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-300' : 'w-5 h-5 text-zinc-600 group-hover:text-white group-hover:translate-x-1 transition-all duration-300'} 
                        />
                      </div>

                      {/* Hover shine effect */}
                      <div className="shine-effect" />
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>

          {/* Footer with enhanced styling */}
          <div className="text-center mt-12 animate-fade-in" style={{ animationDelay: '800ms' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <p className="text-zinc-500 text-xs tracking-wider font-medium">
                @diorgysantos
              </p>
            </div>
            <p className="text-zinc-700 text-xs mt-4">
              Feito com 🎬 e café
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinksBio;